import React from 'react';
import { observer } from 'mobx-react';
import { mutate } from 'swr';
import { regular } from '@fortawesome/fontawesome-svg-core/import.macro';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { ModalStep } from '@components/common/multiStepModal';
import { LightFormBuilder } from '@components/common/lightFormBuilder';
import { ApiGetResource } from '@api';
import useStores from '@stores/useStores';

import { useAccountManagementStore } from '../../store';
import { useOAuth } from '../../hooks/useOAuth';
import { getResourceCacheKey } from '../../hooks/useLoadAccountsIntoAccountManagementStore';

export const AccountTypeFormModalStep: React.FC = observer(() => {
  const { workspaceStore } = useStores();
  const { selectedAccountDefinition, form, getOAuthPayload, setAccountId, resourceDefinitionName } =
    useAccountManagementStore();

  const [refetchingAccounts, setRefetchingAccounts] = React.useState(false);

  const { startOAuth, loading } = useOAuth({
    onComplete: (accountId) => {
      setRefetchingAccounts(true);

      mutate(
        getResourceCacheKey(workspaceStore.id, resourceDefinitionName, ApiGetResource.ACCOUNTS),
      ).finally(() => {
        setAccountId(accountId);
        setRefetchingAccounts(false);
      });
    },
  });

  if (!selectedAccountDefinition) {
    return null;
  }

  const handleOAuthClick = async () => {
    try {
      await form.validateFields();
      await startOAuth(getOAuthPayload());
    } catch (error) {}
  };

  return (
    <ModalStep
      gap="16px"
      header={{
        title: 'Configure account',
        description: `Configure your ${selectedAccountDefinition.uiConfig.name} account`,
      }}
      footer={{
        actionButton: {
          text: 'Connect account',
          icon: <FontAwesomeIcon icon={regular('plug-circle-plus')} />,
          onClick: handleOAuthClick,
        },
        loading: loading || refetchingAccounts,
      }}
    >
      <LightFormBuilder formConfig={selectedAccountDefinition.uiConfig.form} form={form} />
    </ModalStep>
  );
});
